import { useState } from 'react'
import { identifyGuest } from '../lib/api.js'
import { useSessionStore } from '../stores/session.store.js'

/**
 * Tarjeta OPCIONAL de pre-registro mostrada en el menú (mesa y takeout). El
 * comensal anónimo deja nombre + teléfono → alimenta el CRM. Nunca estorba: se
 * descarta con "Ahora no" y jamás bloquea el ordenar.
 *
 * Dos framings, según el toggle de lealtad del negocio (loyaltyProgram.enabled):
 *  - Lealtad ON  : "Identifícate y acumula puntos" (incentivo explícito).
 *  - Lealtad OFF : "Déjanos tus datos" (registro neutro) — IGUAL alimenta el CRM.
 *
 * Estados: prompt (invitación) · form (nombre + teléfono) · earned (confirmación,
 * con puntos si la lealtad está activa, o sólo "registrado" si no).
 */
export function LoyaltyCard() {
  const session = useSessionStore((s) => s.session)
  const loyalty = useSessionStore((s) => s.loyalty)
  const dismissed = useSessionStore((s) => s.loyaltyDismissed)
  const loyaltyProgram = useSessionStore((s) => s.loyaltyProgram)
  const setLoyalty = useSessionStore((s) => s.setLoyalty)
  const dismiss = useSessionStore((s) => s.dismissLoyalty)

  const [mode, setMode] = useState<'prompt' | 'form'>('prompt')
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!session) return null

  // Lealtad OFF explícita → NO mostrar la tarjeta en absoluto (ni el nudge de
  // "acumular puntos" ni la re-entrada). Antes, con OFF la tarjeta seguía visible
  // sólo con copy neutro, y la re-entrada seguía diciendo "acumular puntos" — por
  // eso el comensal veía puntos aunque el negocio hubiera apagado la lealtad. Sólo
  // ocultamos cuando el backend confirma `enabled === false`; mientras carga
  // (undefined) no ocultamos, para no parpadear si va a estar ON.
  if (loyaltyProgram?.enabled === false) return null

  // ¿El negocio autorizó el programa de lealtad? Si SÍ, la tarjeta enmarca el
  // registro como "acumula puntos" (incentivo). Si aún cargando, enmarca el
  // registro de forma NEUTRA.
  const loyaltyOn = loyaltyProgram?.enabled === true
  const ppv = loyaltyProgram?.pointsPerVisit ?? 0
  const promptTitle = loyaltyOn ? 'Identifícate y acumula puntos' : 'Déjanos tus datos'
  const promptSub = loyaltyOn
    ? (ppv > 0 ? `Suma ${ppv} pts de lealtad en cada visita.` : 'Suma puntos de lealtad en cada visita.')
    : 'Regístrate para estar en contacto y agilizar tu próxima visita.'
  const formTitle = loyaltyOn ? 'Acumula puntos' : 'Regístrate'
  const formSub = loyaltyOn
    ? 'Identifícate y suma puntos en cada visita. Tu teléfono es opcional.'
    : 'Déjanos tu nombre para estar en contacto. Tu teléfono es opcional.'
  const submitLabel = loyaltyOn ? 'Acumular puntos' : 'Registrarme'
  const reentryLabel = loyaltyOn ? 'Identificarme y acumular puntos' : 'Dejar mis datos'

  // Ya se identificó en esta sesión → resumen. Con lealtad ON y puntos ganados,
  // muestra los puntos; si no (lealtad OFF), confirma el registro sin puntos.
  if (loyalty) {
    const gotPoints = loyalty.pointsEarned > 0 || loyalty.loyaltyPoints > 0
    return (
      <div className="card loyalty-card loyalty-card--earned">
        <div className="loyalty-card__badge" aria-hidden>{gotPoints ? '★' : '✓'}</div>
        <div className="grow">
          <div style={{ fontWeight: 700 }}>¡Listo, {loyalty.customerName.split(' ')[0]}!</div>
          <div className="muted">
            {gotPoints ? (
              <>
                Acumulaste <strong style={{ color: 'var(--accent-2)' }}>+{loyalty.pointsEarned} pts</strong>
                {' · '}Tienes <strong style={{ color: 'var(--accent-2)' }}>{loyalty.loyaltyPoints} pts</strong>
                {loyalty.loyaltyVisits > 1 ? ` · ${loyalty.loyaltyVisits} visitas` : ''}
              </>
            ) : (
              'Quedaste registrado. ¡Gracias por tu visita!'
            )}
          </div>
        </div>
      </div>
    )
  }

  // El comensal descartó la tarjeta grande — NO insistir con ella, pero dejar
  // SIEMPRE una re-entrada compacta para registrarse después.
  if (dismissed && mode !== 'form') {
    return (
      <button type="button" className="loyalty-reentry" onClick={() => setMode('form')}>
        <span aria-hidden>★</span> {reentryLabel}
      </button>
    )
  }

  async function submit(): Promise<void> {
    if (!session) return
    if (name.trim().length < 2) {
      setError('Escribe tu nombre.')
      return
    }
    setSubmitting(true)
    setError(null)
    const body: { name: string; phone?: string } = { name: name.trim() }
    if (phone.trim()) body.phone = phone.trim()
    const r = await identifyGuest(session.token, body)
    setSubmitting(false)
    if (!r.ok) {
      setError(`No pudimos registrarte: ${r.error.message}`)
      return
    }
    const customerName = r.data.customerName || name.trim()
    if (r.data.loyalty) {
      setLoyalty({ ...r.data.loyalty, customerName })
    } else {
      // CRM no respondió pero la identidad sí quedó — confirmar sin puntos.
      setLoyalty({
        contactId: '',
        loyaltyPoints: 0,
        loyaltyVisits: 1,
        pointsEarned: 0,
        customerName,
      })
    }
  }

  if (mode === 'form') {
    return (
      <div className="card loyalty-card">
        <div style={{ fontWeight: 700, marginBottom: 2 }}>{formTitle}</div>
        <div className="muted" style={{ marginBottom: 10 }}>
          {formSub}
        </div>
        <label className="loyalty-field">
          <span>Nombre</span>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Tu nombre"
            autoComplete="given-name"
            maxLength={80}
          />
        </label>
        <label className="loyalty-field">
          <span>Teléfono (opcional)</span>
          <input
            className="input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="55 1234 5678"
            inputMode="tel"
            autoComplete="tel"
            maxLength={32}
          />
        </label>
        {error && <div className="danger" style={{ fontSize: 13, marginTop: 4 }}>{error}</div>}
        <div className="row" style={{ gap: 8, marginTop: 10 }}>
          <button
            className="btn btn-ghost"
            style={{ flex: '0 0 auto', width: 'auto' }}
            onClick={() => { setMode('prompt'); setError(null) }}
            disabled={submitting}
          >
            Cancelar
          </button>
          <button className="btn btn-primary grow" onClick={submit} disabled={submitting}>
            {submitting ? <><span className="spinner" /> Registrando…</> : submitLabel}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="card loyalty-card loyalty-card--prompt">
      <div className="loyalty-card__badge" aria-hidden>{loyaltyOn ? '★' : '✦'}</div>
      <div className="grow">
        <div style={{ fontWeight: 700 }}>{promptTitle}</div>
        <div className="muted">{promptSub}</div>
      </div>
      <div className="row" style={{ gap: 8, flex: '0 0 auto' }}>
        <button
          className="btn btn-ghost"
          style={{ width: 'auto', padding: '8px 10px' }}
          onClick={dismiss}
        >
          Ahora no
        </button>
        <button
          className="btn btn-outline"
          style={{ width: 'auto', padding: '8px 12px' }}
          onClick={() => setMode('form')}
        >
          {loyaltyOn ? 'Identificarme' : 'Registrarme'}
        </button>
      </div>
    </div>
  )
}
